import React from 'react';
import { OnboardingIdentity } from '../../../services/aiIntelligenceService';

const COACHING_STYLES = [
  { value: 'player-centred', label: 'Player-Centred', description: 'Focus on individual development and player needs' },
  { value: 'possession-based', label: 'Possession-Based', description: 'Emphasis on keeping the ball and building from the back' },
  { value: 'direct', label: 'Direct', description: 'Quick transitions and forward play' },
  { value: 'balanced', label: 'Balanced', description: 'Adaptable approach mixing styles based on context' },
  { value: 'high-press', label: 'High Press', description: 'Aggressive pressing and winning the ball high up the pitch' },
];

const STYLES_OF_PLAY = [
  { value: 'attacking', label: 'Attacking', description: 'Prioritise creating chances and scoring goals' },
  { value: 'defensive', label: 'Defensive', description: 'Solid defensive structure with counter-attacking' },
  { value: 'possession', label: 'Possession', description: 'Control the game through ball retention' },
  { value: 'counter-attacking', label: 'Counter-Attacking', description: 'Absorb pressure and exploit transitions' },
  { value: 'flexible', label: 'Flexible', description: 'Adapt style based on opposition and game state' },
];

interface IdentityStepProps {
  data: OnboardingIdentity;
  onChange: (data: OnboardingIdentity) => void;
}

export function IdentityStep({ data, onChange }: IdentityStepProps) {
  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Coaching Identity</h2>
      <p className="text-sm text-gray-600 mb-6">
        How would you describe your coaching approach and preferred style of play?
      </p>

      {/* Coaching Style */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Coaching Style <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {COACHING_STYLES.map((style) => (
            <button
              key={style.value}
              type="button"
              onClick={() => onChange({ ...data, coachingStyle: style.value })}
              className={`text-left p-3 rounded-lg border-2 transition-all min-h-[44px] ${
                data.coachingStyle === style.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">{style.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{style.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Style of Play */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Style of Play <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {STYLES_OF_PLAY.map((style) => (
            <button
              key={style.value}
              type="button"
              onClick={() => onChange({ ...data, styleOfPlay: style.value })}
              className={`text-left p-3 rounded-lg border-2 transition-all min-h-[44px] ${
                data.styleOfPlay === style.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">{style.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{style.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Description (optional) */}
      <div>
        <label htmlFor="identity-description" className="block text-sm font-medium text-gray-700 mb-2">
          Additional Notes <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          id="identity-description"
          value={data.description || ''}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          placeholder="Anything else about your coaching philosophy..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
      </div>
    </div>
  );
}
